import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useModal } from 'hooks/useModal';
import React, { useEffect, useState } from 'react';
import {
  LinkConfirmModal,
  UnlinkConfirmModal,
} from './components/LinkConfirmModal';
import { SNSIcon, snsToString } from './components/SNS';
import { SNS } from './components/SNS';
import * as api from 'config/api';
import * as useAPI from 'hooks/useAPI';
import { SNSTypes } from 'config/consts';
import Loading from 'components/commons/loading/Loading';
import { SNSErrorModal } from './components/SNSErrorModal';
import { snslinkages } from 'config/Routes';

export type SNSLinkageStatus =
  | {
      tag: 'Uncertified';
      linkURL: string;
    }
  | {
      tag: 'Failed';
    }
  | {
      tag: 'Certified';
      name: string;
      icon: string;
    };

export type SNSLinkageType =
  | {
      tag: 'Uncertified';
      certificate: () => Promise<void>;
    }
  | {
      tag: 'Failed';
    }
  | {
      tag: 'Certified';
      uncertificate: () => Promise<{ tag: 'Success' | 'Failure' }>;
      share: (
        dataURL: string,
        comment: string
      ) => Promise<{ tag: 'Success' | 'Failure' }>;
    };

type SNSLinkageModal = {
  tag: SNSLinkageStatus['tag'];
  showModal: () => void;
  Modal: () => React.ReactElement;
};

const useSNSLinkageModal = (
  sns: SNS,
  status: useAPI.State<'status', SNSLinkageStatus>,
  refresh: () => void,
  unlink: () => void
): useAPI.State<'modal', SNSLinkageModal> => {
  const [isShowModal, modal] = useModal();
  const linkage = useSNSLinkage(sns, status);

  const Modal = () => {
    if (useAPI.isLoading(status) || useAPI.isLoading(linkage)) return <></>;

    switch (status.status.tag) {
      case 'Uncertified':
        return (
          <LinkConfirmModal
            {...{
              hide: modal.hideModal,
              isShow: isShowModal,
              onCancel: modal.hideModal,
              onClickSNSLink: async () => {
                linkage.linkage.tag === 'Uncertified' &&
                  (await linkage.linkage.certificate());
                modal.hideModal();
              },
              sns,
            }}
          />
        );
      case 'Failed':
        return (
          <SNSErrorModal {...{ isShowModal, hideModal: modal.hideModal }} />
        );
      case 'Certified':
        return (
          <UnlinkConfirmModal
            {...{
              hide: modal.hideModal,
              isShow: isShowModal,
              onCancel: modal.hideModal,
              sns,
              onUncertificate: async () => {
                linkage.linkage.tag === 'Certified' &&
                  (await linkage.linkage.uncertificate().then(({ tag }) => {
                    if (tag === 'Success') {
                      refresh();
                      unlink();
                    }
                  }));
                modal.hideModal();
              },
            }}
          />
        );
    }
  };

  if (useAPI.isLoading(status) || useAPI.isLoading(linkage))
    return useAPI.loading;

  return useAPI.loaded({
    modal: {
      tag: status.status.tag,
      Modal,
      showModal: modal.showModal,
    },
  });
};

const useSNSAPI = (redirectTo: string) => {
  const [state, setState] = useState<
    useAPI.State<'state', api.GetSNSAccountResult>
  >(useAPI.loading);
  const get = async () => {
    const ret = await api.getSNSAccount(encodeURI(redirectTo));
    setState(useAPI.loaded({ state: ret }));
  };
  useEffect(() => {
    get();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const refresh = () => {
    setState(useAPI.loading);
    get();
  };
  return { state, refresh };
};

export const useSNSLinkageStatus = (
  sns: SNS,
  redirectTo: string
): useAPI.State<'state', { status: SNSLinkageStatus; refresh: () => void }> => {
  const { state, refresh } = useSNSAPI(redirectTo);
  if (useAPI.isLoading(state)) return useAPI.loading;

  if (!state.state)
    return useAPI.loaded({
      state: { status: { tag: 'Failed' as const }, refresh },
    });
  const snsItem = state.state.Items.find(item => {
    return item.SNSType === SNSTypes[sns];
  });

  if (!snsItem)
    return useAPI.loaded({
      state: { status: { tag: 'Failed' as const }, refresh },
    });

  return snsItem.Name && snsItem.Linked && snsItem.ProfileImageURL
    ? useAPI.loaded({
        state: {
          status: {
            tag: 'Certified' as const,
            name: snsItem.Name,
            icon: snsItem.ProfileImageURL,
          },
          refresh,
        },
      })
    : useAPI.loaded({
        state: {
          status: {
            tag: 'Uncertified' as const,
            linkURL: snsItem.LinkURL,
          },
          refresh,
        },
      });
};

export const useSNSLinkage = (
  sns: SNS,
  status: useAPI.State<'status', SNSLinkageStatus>
): useAPI.State<'linkage', SNSLinkageType> => {
  if (useAPI.isLoading(status)) return useAPI.loading;

  const status_ = status.status;
  switch (status_.tag) {
    case 'Certified':
      return useAPI.loaded({
        linkage: {
          tag: status_.tag,
          share: async (dataURL: string, comment: string) => {
            const ret = await api.postSNSImage({
              ImageData: dataURL,
              SNSType: SNSTypes[sns],
              Text: comment,
            });
            return ret.Success
              ? { tag: 'Success' as const }
              : { tag: 'Failure' as const };
          },
          uncertificate: async () => {
            const ret = await api.unlinkSNSAccount({ SNSType: SNSTypes[sns] });
            return ret.Success
              ? { tag: 'Success' as const }
              : { tag: 'Failure' as const };
          },
        },
      });
    case 'Failed':
      return useAPI.loaded({
        linkage: {
          tag: 'Failed' as const,
        },
      });
    case 'Uncertified':
      return useAPI.loaded({
        linkage: {
          tag: status_.tag,
          certificate: async () => {
            window.location.href = status_.linkURL;
          },
        },
      });
  }
};

const SNSLinkage = ({
  snsLinkageStatus,
  sns,
  className,
  refreshLinkageStatus,
  unlink,
}: {
  snsLinkageStatus: SNSLinkageStatus;
  sns: SNS;
  refreshLinkageStatus: () => void;
  unlink: () => void;
  className?: string;
}) => {
  const modal_ = useSNSLinkageModal(
    sns,
    useAPI.loaded({ status: snsLinkageStatus }),
    refreshLinkageStatus,
    unlink
  );
  if (useAPI.isLoading(modal_)) return <Loading loading />;
  const {
    modal: { Modal, ...modal },
  } = modal_;
  return (
    <>
      <div
        className={`grid p-4 bg-white ${className ?? ''}`}
        style={{ gridTemplateColumns: 'auto 1fr auto' }}>
        <div className={`flex flex-col mr-4`}>
          <SNSIcon {...{ sns }} />
        </div>
        <div className={`flex flex-col`}>
          <div className={`text-sm`}>{snsToString(sns)}</div>
          <div className={`mt-1`}>
            {(() => {
              switch (snsLinkageStatus.tag) {
                case 'Certified':
                  return (
                    <div className={`flex flex-row items-center`}>
                      <div className="font-bold">認証済み：</div>
                      <div className="font-normal">{snsLinkageStatus.name}</div>
                    </div>
                  );
                case 'Uncertified':
                  return <div className="font-bold">未認証</div>;
                case 'Failed':
                  return (
                    <div className="font-bold text-tradewell">
                      サーバーエラー
                    </div>
                  );
              }
            })()}
          </div>
        </div>
        <div className={`flex flex-col text-sm`}>
          {(() => {
            switch (modal.tag) {
              case 'Certified':
                return (
                  <button
                    className="text-bluegray-500"
                    onClick={modal.showModal}>
                    連携を解除
                  </button>
                );
              case 'Uncertified':
                return (
                  <button className="text-blue-700" onClick={modal.showModal}>
                    連携する
                  </button>
                );
              case 'Failed':
                return (
                  <button
                    className="text-bluegray-500"
                    onClick={modal.showModal}>
                    連携する
                  </button>
                );
            }
          })()}
        </div>
      </div>
      <Modal />
    </>
  );
};

type State = 'Ready' | 'Unlinked';

const UnlinkedStickyNote = () => {
  return (
    <>
      <div
        className={`border-t-2 rounded-b-lg p-4 flex flex-row shadow`}
        style={{ borderColor: `#00947d`, backgroundColor: `#f0fdfa` }}>
        <div className={`flex items-center justify-center`}>
          <div
            className={`icon-check text-white rounded-full mr-2`}
            style={{ backgroundColor: `#00947d` }}></div>
        </div>
        <div className="mr-auto text-base font-bold">解除が完了しました</div>
      </div>
    </>
  );
};

export const SNSLinkageList = () => {
  const { setCustomTitle } = useCustomizeHeaderContext();
  useEffect(() => {
    setCustomTitle('SNS連携一覧');
    return () => setCustomTitle('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [state, setState] = useState<State>('Ready');
  const twitter = useSNSLinkageStatus('twitter', snslinkages);
  const unlink = () => setState('Unlinked');
  if (useAPI.isLoading(twitter))
    return <Loading loading style={{ paddingTop: '10rem' }} />;
  return (
    <>
      <div className="bg-white h-full">
        {state === 'Unlinked' && (
          <>
            <div className={`py-2 px-3`}>
              <UnlinkedStickyNote />
            </div>
          </>
        )}
        <SNSLinkage
          sns="twitter"
          snsLinkageStatus={twitter.state.status}
          className="border-b border-bluegray-300"
          refreshLinkageStatus={twitter.state.refresh}
          unlink={unlink}
        />
      </div>
    </>
  );
};
